// GENOME TEXTURE T (APP_GENSURF §5e, gs-gpupainter): the plain-JS twin of
// pipeline/surfacegen.py genome_texture / genome_texture_params (its
// docstring is the PORT CONTRACT), written per texel so the GLSL port is
// line by line:
//   - genomeTexParams(rec, key, table) -> the texel-independent numbers of
//     one genome record (C4): octave gains g (the in-band deflation kd, the
//     band-share solve, the aniso stretch a), the remap knots, the palette in
//     linear RGB + its cuts, the seeds — what a shader gets as uniforms. The
//     solve runs in float64 with the oracle's fixed iterations (= numpy to
//     ~1e-9);
//   - genomeTexelConsts(P, texel) -> the per-(record, texel) uniforms: the
//     faded octave coefficients al / be (float32) and the palette normalised
//     by E_texel[colour] (float32), so T's mean is 1 per channel at every level;
//   - genomeTexel(P, x, z, texel) -> [r, g, b] the linear multiplier at world
//     (x, z) (game metres): float32 where numpy is (Math.fround per op), the
//     uint32 lattice hash of surfacegen._gnoise (Math.imul / >>> 0), the
//     metric + lattice coordinates in float64.
// table = pipeline/genome_noise.json (noise_table.py; Gaussianising table +
// octave -> band / tensor responses), version-checked: fails fast.
import GNT from '../../pipeline/genome_noise.json' with { type: 'json' };   // node-loadable (the gates import the pool in-realm: poolwork -> pool-genome)

const f32 = Math.fround;

// ---------------------------------------------------------------- constants (surfacegen GT_*, genome GN_*)
const GT_VER = 1;
const GT_OCT = 8;
const GN_PX = 0.25;
const GT_BAND0_M = 2.0 * Math.sqrt(2.0) * GN_PX;
const GT_WL_K = 0.5;
const GT_SOLVE_ITERS = 500;
const GT_ANISO_ITERS = 12;
const GT_SLOPE = [0.25, 4.0];
const GT_A_MAX = 8.0;
const GT_QUAD_N = 801;
const GT_QUAD_Z = 8.0;
const GT_GAIN_ITERS = 4;
const GT_DEFLATE_ITERS = 4;
const GT_PAIR_N = 161;
const GT_PAIR_K = 4.0 / 3.0;
const GT_SEED_MOD = 251;
const GT_SEED_BASE = 300;
const GT_SEED_STRIDE = 10;
const GN_SYN_PATCH = 0.25;
const GN_SYN_PATCH_M = 4.0;
const GN_Q = [0.005, ...Array.from({ length: 15 }, (_, i) => (i + 1) / 16), 0.995];
const LUMA = [0.2126, 0.7152, 0.0722];
const WHITE = [0.95047, 1.0, 1.08883];
const M_XYZ = [[0.4124564, 0.3575761, 0.1804375], [0.2126729, 0.7151522, 0.0721750], [0.0193339, 0.1191920, 0.9503041]];
const M_INV = (() => {
  const [[a, b, c], [d, e, f], [g, h, i]] = M_XYZ;
  const A = e * i - f * h, B = -(d * i - f * g), C = d * h - e * g, det = a * A + b * B + c * C;
  return [[A / det, -(b * i - c * h) / det, (b * f - c * e) / det],
    [B / det, (a * i - c * g) / det, -(a * f - c * d) / det],
    [C / det, -(a * h - b * g) / det, (a * e - b * d) / det]];
})();

// ---------------------------------------------------------------- the table
const TABLES = new WeakMap();
function gtTable(t) {
  let T = TABLES.get(t);
  if (T) return T;
  if (t.ver !== GT_VER || t.wl_k !== GT_WL_K || t.bands[0][0].length !== GT_OCT)
    throw new Error(`genome_noise.json: ver ${t.ver} / wl_k ${t.wl_k} / ${t.bands[0][0].length} octaves is not ${GT_VER} / ${GT_WL_K} / ${GT_OCT}`);
  T = { gz: Float32Array.from(t.gauss.z), ag: t.a.map(Math.log), th: t.theta_deg, bands: t.bands, tensor: t.tensor };
  TABLES.set(t, T);
  return T;
}

// ---------------------------------------------------------------- noise (surfacegen._gnoise, float32 per op)
const PHI = 0.6180339887498949;
const GRAD_X = Float32Array.from({ length: 256 }, (_, k) => Math.cos((k + 0.5) * (2 * Math.PI / 256)));
const GRAD_Z = Float32Array.from({ length: 256 }, (_, k) => Math.sin((k + 0.5) * (2 * Math.PI / 256)));
function grad(hx, hz, dx, dz) {
  let h = (hx + hz) >>> 0;
  h = Math.imul((h ^ (h >>> 13)) >>> 0, 1274126177) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  const k = h >>> 24;
  return f32(f32(GRAD_X[k] * dx) + f32(GRAD_Z[k] * dz));
}
const fade = (t) => f32(f32(f32(f32(t * t) * t)) * f32(f32(t * f32(f32(t * 6) - 15)) + 10));
export function gnoise32(x, z, wl, seed) {
  const a = (15.0 + 60.0 * ((seed * PHI) % 1.0)) * (Math.PI / 180.0);
  const c = Math.cos(a), s = Math.sin(a);
  const fx = (x * c - z * s) * (1.0 / wl) + seed * 17.31;
  const fz = (x * s + z * c) * (1.0 / wl) + seed * 9.77;
  const ix = Math.floor(fx), iz = Math.floor(fz);
  const tx = f32(fx - ix), tz = f32(fz - iz);
  const hx0 = Math.imul(ix | 0, 374761393) >>> 0;
  const hz0 = (Math.imul(iz | 0, 668265263) + ((seed * 40503) >>> 0)) >>> 0;
  const hx1 = (hx0 + 374761393) >>> 0, hz1 = (hz0 + 668265263) >>> 0;
  const u = fade(tx), v = fade(tz), iu = f32(1 - u), iv = f32(1 - v);
  const tx1 = f32(tx - 1), tz1 = f32(tz - 1);
  const n0 = f32(f32(grad(hx0, hz0, tx, tz) * iu) + f32(grad(hx1, hz0, tx1, tz) * u));
  const n1 = f32(f32(grad(hx0, hz1, tx, tz1) * iu) + f32(grad(hx1, hz1, tx1, tz1) * u));
  const n = f32(f32(n0 * iv) + f32(n1 * v));
  const o = f32(0.5 + n);
  return o < 0 ? 0 : o > 1 ? 1 : o;
}
// _gt_gauss: _gnoise output -> N(0, 1) through the table's knots (linear)
function gauss(gz, n) {
  const N = gz.length - 1, u = f32(n * N);
  const i0 = Math.min(Math.max(Math.floor(u), 0), N - 1);
  const t = f32(u - i0);
  return f32(f32(gz[i0] * f32(1 - t)) + f32(gz[i0 + 1] * t));
}

// ---------------------------------------------------------------- float64 helpers
// Acklam's inverse normal CDF (surfacegen._ndtri)
const NA = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02,
  -3.066479806614716e+01, 2.506628277459239e+00];
const NB = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01,
  -1.328068155288572e+01];
const NC = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00,
  4.374664141464968e+00, 2.938163982698783e+00];
const ND = [7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];
function ndtri(p) {
  const lo = 0.02425;
  const q = Math.sqrt(-2.0 * Math.log(Math.max(Math.min(p, 1.0 - p), 1e-300)));
  const tail = (((((NC[0] * q + NC[1]) * q + NC[2]) * q + NC[3]) * q + NC[4]) * q + NC[5])
    / ((((ND[0] * q + ND[1]) * q + ND[2]) * q + ND[3]) * q + 1.0);
  if (p < lo) return tail;
  if (p > 1.0 - lo) return -tail;
  const r = p - 0.5, s = r * r;
  return (((((NA[0] * s + NA[1]) * s + NA[2]) * s + NA[3]) * s + NA[4]) * s + NA[5]) * r
    / (((((NB[0] * s + NB[1]) * s + NB[2]) * s + NB[3]) * s + NB[4]) * s + 1.0);
}
// normal CDF, Abramowitz-Stegun 7.1.26 (surfacegen._ndtr)
function ndtr(x) {
  const u = Math.abs(x) / Math.sqrt(2.0), t = 1.0 / (1.0 + 0.3275911 * u);
  const y = 1.0 - (((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t
    + 0.254829592) * t) * Math.exp(-u * u);
  return 0.5 * (1.0 + (x < 0 ? -y : y));
}
// np.linspace(-GT_QUAD_Z, GT_QUAD_Z, n)
function linspace(n) {
  const step = (2 * GT_QUAD_Z) / (n - 1), z = new Float64Array(n);
  for (let i = 0; i < n; i++) z[i] = i * step - GT_QUAD_Z;
  z[n - 1] = GT_QUAD_Z;
  return z;
}
function normW(z) {
  const w = z.map((v) => Math.exp(-0.5 * v * v));
  let s = 0;
  for (const v of w) s += v;
  return w.map((v) => v / s);
}
const QZ = linspace(GT_QUAD_N), QW = normW(QZ);                    // _gt_quad
const PZ = linspace(GT_PAIR_N), PW = normW(PZ);
// _gt_remap: piecewise linear through (zk, yk) (np.interp), linear past the ends
function remap(z, zk, yk) {
  const n = zk.length;
  if (z < zk[0]) return yk[0] + (z - zk[0]) * ((yk[1] - yk[0]) / Math.max(zk[1] - zk[0], 1e-12));
  if (z > zk[n - 1]) return yk[n - 1] + (z - zk[n - 1]) * ((yk[n - 1] - yk[n - 2]) / Math.max(zk[n - 1] - zk[n - 2], 1e-12));
  if (z === zk[n - 1]) return yk[n - 1];
  let j = 0;
  while (j < n - 2 && zk[j + 1] <= z) j++;
  return ((yk[j + 1] - yk[j]) / (zk[j + 1] - zk[j])) * (z - zk[j]) + yk[j];
}
export function labLin(L) {                                      // genome.lab_lin
  const fy = (L[0] + 16) / 116, f = [fy + L[1] / 500, fy, fy - L[2] / 200], d = 6 / 29, xyz = [0, 0, 0];
  for (let k = 0; k < 3; k++) xyz[k] = (f[k] > d ? f[k] ** 3 : 3 * d * d * (f[k] - 4 / 29)) * WHITE[k];
  return [0, 1, 2].map((k) => xyz[0] * M_INV[k][0] + xyz[1] * M_INV[k][1] + xyz[2] * M_INV[k][2]);
}
const octaveKeep = (wl, texel) => {                             // surfacegen._octave_keep
  const t = Math.min(Math.max((wl / texel - 2.0) / 2.0, 0.0), 1.0);
  return t * t * (3 - 2 * t);
};

// ---------------------------------------------------------------- the solve (float64)
// (bands (GT_OCT x 8), tensor (GT_OCT x 2)) at stretch a, streak deg (_gt_resp)
function resp(T, a, deg) {
  const ag = T.ag, th = T.th;
  const u = Math.min(Math.max(Math.log(Math.max(a, 1e-9)), ag[0]), ag[ag.length - 1]);
  let i = -1;
  while (i + 1 < ag.length && ag[i + 1] <= u) i++;
  i = Math.min(i, ag.length - 2);
  const f = (u - ag[i]) / (ag[i + 1] - ag[i]);
  let d = ((deg % 90.0) + 90.0) % 90.0;
  d = Math.min(d, 90.0 - d);
  let j = -1;
  while (j + 1 < th.length && th[j + 1] <= d) j++;
  j = Math.min(j, th.length - 2);
  const e = Math.min(Math.max((d - th[j]) / (th[j + 1] - th[j]), 0.0), 1.0);
  const at = (X) => X[0][0].map((_, o) => X[0][0][o].map((_, b) =>
    (X[i][j][o][b] * (1 - e) + X[i][j + 1][o][b] * e) * (1 - f) + (X[i + 1][j][o][b] * (1 - e) + X[i + 1][j + 1][o][b] * e) * f));
  return [at(T.bands), at(T.tensor)];
}
// octave shares w >= 0: Lee-Seung NNLS on the matched bands [+ sum w = 1] (_gt_shares)
function shares(R, tgt, free, norm) {
  const m = [];
  for (let b = 0; b < tgt.length; b++) if (tgt[b] !== null) m.push(b);
  const cols = m.concat(free), nr = m.length + (norm ? 1 : 0), nc = cols.length;
  const A = Array.from({ length: nr }, (_, r) => cols.map((o) => (r < m.length ? R[o][m[r]] / tgt[m[r]] : 1.0)));
  const Aty = cols.map((_, c) => { let s = 0; for (let r = 0; r < nr; r++) s += A[r][c]; return s; });
  const AtA = cols.map((_, c) => cols.map((__, k) => { let s = 0; for (let r = 0; r < nr; r++) s += A[r][c] * A[r][k]; return s; }));
  let w = new Array(nc).fill(1.0 / nc);
  for (let it = 0; it < GT_SOLVE_ITERS; it++) {
    const nw = new Array(nc);
    for (let c = 0; c < nc; c++) {
      let s = 0;
      for (let k = 0; k < nc; k++) s += AtA[c][k] * w[k];
      nw[c] = w[c] * Aty[c] / Math.max(s, 1e-300);
    }
    w = nw;
  }
  const out = new Array(GT_OCT).fill(0);
  cols.forEach((o, c) => { out[o] = w[c]; });
  return out;
}
// E[(M(Z1) - M(Z2))^2] / E[(Z1 - Z2)^2], corr(Z1, Z2) = rho (_gt_pair_gain)
function pairGain(rho, zk, yk) {
  rho = Math.min(Math.max(rho, 0.0), 1.0 - 1e-6);
  const r2 = Math.sqrt(1.0 - rho * rho), n = PZ.length;
  let s = 0;
  for (let i = 0; i < n; i++) {
    const m1 = remap(PZ[i], zk, yk);
    for (let j = 0; j < n; j++) {
      const dm = m1 - remap(rho * PZ[i] + r2 * PZ[j], zk, yk);
      s += PW[i] * PW[j] * dm * dm;
    }
  }
  return s / (2.0 * (1.0 - rho));
}
// band targets in Z units through the remap's pair gain (_gt_targets)
function targets(spec, V, zk, yk) {
  let tgt = spec.map((s) => (s ? s * s / Math.max(V, 1e-12) : null));
  for (let it = 0; it < GT_GAIN_ITERS; it++) {
    let cum = 0;
    tgt = spec.map((s, b) => {
      if (tgt[b] === null) return null;
      cum += tgt[b];
      return s * s / Math.max(pairGain(1.0 - GT_PAIR_K * cum, zk, yk), 1e-12);
    });
  }
  return tgt;
}
// -> [a, w]: the shares + the aniso stretch by secant steps (_gt_solve)
function solve(T, spec, V, zk, yk, deg, want, norm) {
  const tgt = targets(spec, V, zk, yk);
  const free = [];
  for (let o = 0; o < GT_OCT; o++) if (tgt[o] === null) free.push(o);
  const dot = (w, X, c) => w.reduce((s, v, o) => s + v * X[o][c], 0);
  let a = Math.min(Math.sqrt(want), GT_A_MAX), xa = null, ya = null;
  for (let it = 0; it < (want > 1.0 ? GT_ANISO_ITERS : 0); it++) {
    const [R, J] = resp(T, a, deg);
    const w = shares(R, tgt, free, norm);
    const x = Math.log(a);
    const y = Math.log(Math.max(dot(w, J, 0) / Math.max(dot(w, J, 1), 1e-30), 1e-9));
    let sl = xa === null || Math.abs(x - xa) < 1e-9 ? 2.0 : (y - ya) / (x - xa);
    sl = Math.min(GT_SLOPE[1], Math.max(GT_SLOPE[0], sl));
    xa = x; ya = y;
    a = Math.min(GT_A_MAX, Math.max(1.0, Math.exp(x + (Math.log(want) - y) / sl)));
  }
  const [R] = resp(T, a, deg);
  return [a, shares(R, tgt, free, norm)];
}
const quadMean = (fn) => { let s = 0; for (let i = 0; i < QZ.length; i++) s += QW[i] * fn(QZ[i]); return s; };
function quadVar(zk, yk) {
  let s1 = 0, s2 = 0;
  for (let i = 0; i < QZ.length; i++) { const m = remap(QZ[i], zk, yk); s1 += QW[i] * m; s2 += QW[i] * m * m; }
  return s2 - s1 * s1;
}
// the knots deflated by kd, E[Y] kept (_gt_knots) -> [yk, V]
function knots(y0, kd, zk, ey) {
  let yk = y0.map((v) => kd * v);
  const c = Math.log(ey / quadMean((z) => Math.exp(remap(z, zk, yk))));
  yk = yk.map((v) => v + c);
  return [yk, quadVar(zk, yk)];
}

// ---------------------------------------------------------------- the record
// 1 + FNV-1a 32 (UTF-8) % GT_SEED_MOD (surfacegen.genome_seed)
export function genomeSeed(key) {
  let h = 2166136261;
  for (const ch of new TextEncoder().encode(String(key))) h = Math.imul(h ^ ch, 16777619) >>> 0;
  return 1 + (h % GT_SEED_MOD);
}

// genome_texture_params: the texel-independent uniforms of one record
export function genomeTexParams(rec, key, table = GNT) {
  const T = gtTable(table);
  const zk = GN_Q.map(ndtri);
  const y0 = rec.lum_q.map((q) => Math.log(Math.max(q, 1e-6)));
  const V0 = quadVar(zk, y0), ey = quadMean((z) => Math.exp(remap(z, zk, y0)));
  const spec = Array.from({ length: 8 }, (_, b) => ((rec.spectrum || [])[b] || null));
  const have = [];
  for (let b = 0; b < 8; b++) if (spec[b]) have.push(b);
  const an = rec.aniso || { deg: 0.0, ratio: 1.0 };
  const deg = Number(an.deg), want = Math.max(1.0, Number(an.ratio));
  let kd = 1.0, yk = y0, V = V0, g = new Array(GT_OCT).fill(0), a = Math.min(Math.sqrt(want), GT_A_MAX);
  if (have.length) {
    // the in-band deflation (lum_q's spread past band 7 is the residual's):
    // null bands continued from the nearest measured band (ties: the finer)
    const full = spec.map((s, b) => s || spec[have.reduce((m, c) => (Math.abs(c - b) < Math.abs(m - b) ? c : m), have[0])]);
    kd = Math.min(1.0, Math.sqrt(full.reduce((s, v) => s + v * v, 0) / V0));
    let w;
    for (let it = 0; it < GT_DEFLATE_ITERS; it++) {
      [yk, V] = knots(y0, kd, zk, ey);
      [a, w] = solve(T, full, V, zk, yk, deg, want, false);
      kd = Math.min(1.0, kd * Math.sqrt(w.reduce((s, v) => s + v, 0)));
    }
    [yk, V] = knots(y0, kd, zk, ey);
    [a, w] = kd < 1.0 ? solve(T, full, V, zk, yk, deg, want, false) : solve(T, spec, V, zk, yk, deg, want, true);
    const ws = Math.max(w.reduce((s, v) => s + v, 0), 1e-30);
    g = w.map((v) => Math.sqrt(v / ws));
  }
  const lam = Array.from({ length: GT_OCT }, (_, o) => GT_BAND0_M * 2.0 ** o);
  const patch = Number(rec.patch_m || GN_SYN_PATCH_M);
  const h = lam.map((l) => Math.exp(-2.0 * (Math.PI * GN_SYN_PATCH * patch / l) ** 2));
  const rgb = rec.palette.map((p) => labLin(p.lab).map((v) => Math.max(v, 1e-4)));
  const wp = rec.palette.map((p) => p.w), wsum = wp.reduce((s, v) => s + v, 0);
  const cuts = [];
  for (let e = 0, c = 0; e < wp.length - 1; e++) { c += wp[e]; cuts.push(ndtri(c / wsum)); }
  const lye = rgb.map((c) => Math.log(c[0] * LUMA[0] + c[1] * LUMA[1] + c[2] * LUMA[2]));
  const seed = genomeSeed(key);
  return {
    key, seed, oseed: lam.map((_, o) => GT_SEED_BASE + GT_SEED_STRIDE * seed + o),
    wl: lam.map((l) => GT_WL_K * l), g, a, deg, kd, V, h, lam, zk, yk, rgb, lye, cuts, gz: T.gz,
    // the metric frame (genome_metric): u = (x sin - z cos) / sqrt a, v = (x cos + z sin) sqrt a
    sin: Math.sin(deg * (Math.PI / 180)), cos: Math.cos(deg * (Math.PI / 180)), sqa: Math.sqrt(a),
  };
}

// genome_texel_consts: the per-(record, texel) uniforms
const CONSTS = new WeakMap();
export function genomeTexelConsts(P, texel) {
  let byTexel = CONSTS.get(P);
  if (!byTexel) CONSTS.set(P, (byTexel = new Map()));
  let C = byTexel.get(texel);
  if (C) return C;
  const { g, h } = P;
  const k = P.lam.map((l) => octaveKeep(Math.sqrt(2.0) * l, texel));
  const sp = Math.sqrt(g.reduce((s, v, o) => s + (v * h[o]) ** 2, 0));
  const al = g.map((v, o) => v * k[o]);
  const be = g.map((v, o) => (sp > 1e-12 ? v * h[o] * k[o] / sp : 0));
  let s2 = 0, p2 = 0, q = 0;
  for (let o = 0; o < GT_OCT; o++) { s2 += al[o] * al[o]; p2 += be[o] * be[o]; q += al[o] * be[o]; }
  const s = Math.sqrt(s2);
  const tau = Math.sqrt(Math.max(p2 - (s2 > 1e-12 ? q * q / s2 : 0.0), 0.0));
  const n = P.rgb.length;
  const chroma = P.rgb.map((c, e) => c.map((v) => v / Math.exp(P.lye[e])));
  const Ec = [0, 0, 0];
  for (let i = 0; i < QZ.length; i++) {
    const z = s * QZ[i], Y = Math.exp(remap(z, P.zk, P.yk)), mu = s2 > 1e-12 ? (q / s2) * z : 0;
    for (let e = 0; e < n; e++) {
      const lo = e > 0 ? P.cuts[e - 1] : -Infinity, hi = e < n - 1 ? P.cuts[e] : Infinity;
      const pr = tau > 1e-9
        ? (Number.isFinite(hi) ? ndtr((hi - mu) / tau) : 1.0) - (Number.isFinite(lo) ? ndtr((lo - mu) / tau) : 0.0)
        : (mu >= lo && mu < hi ? 1 : 0);
      for (let c = 0; c < 3; c++) Ec[c] += QW[i] * Y * pr * chroma[e][c];
    }
  }
  C = {
    al: Float32Array.from(al), be: Float32Array.from(be), Ec,
    pal: chroma.map((c) => Float32Array.from(c.map((v, ch) => v / Math.max(Ec[ch], 1e-30)))),
  };
  byTexel.set(texel, C);
  return C;
}

// genome_texture at one texel: [r, g, b] linear multiplier (mean 1 per channel)
export function genomeTexel(P, x, z, texel) {
  const C = genomeTexelConsts(P, texel);
  const u = (x * P.sin - z * P.cos) / P.sqa, v = (x * P.cos + z * P.sin) * P.sqa;
  let zs = 0, zp = 0;
  for (let o = 0; o < GT_OCT; o++) {
    if (C.al[o] === 0 && C.be[o] === 0) continue;
    const n = gauss(P.gz, gnoise32(u, v, P.wl[o], P.oseed[o]));
    zs = f32(zs + f32(C.al[o] * n));
    zp = f32(zp + f32(C.be[o] * n));
  }
  const ey = f32(Math.exp(f32(remap(zs, P.zk, P.yk))));
  let e = 0;
  while (e < P.cuts.length && P.cuts[e] <= zp) e++;
  const pal = C.pal[e];
  return [f32(pal[0] * ey), f32(pal[1] * ey), f32(pal[2] * ey)];
}
